import { useUserRole } from '@mairistem/finances-utils';
import { newIdentifiant } from '@mairistem/tools';
import React from 'react';
import { useHistory } from 'react-router';
import { Table, type TableInstance } from 'rsuite-table';
import TreeIndex from '../../../classes/pieces/TreeIndex';
import TreeRowFilter from '../../../classes/pieces/TreeRowFilter';
import TreeViewFactory from '../../../classes/pieces/TreeViewFactory';
import BordereauStateType from '../../../constants/liquidation/bordereaux/BordereauStateType';
import TreeNodeType from '../../../constants/liquidation/TreeNodeType';
import type TreeViewType from '../../../constants/liquidation/TreeViewType';
import type SensOptions from '../../../interfaces/pieces/bordereaux/SensOptions';
import type {
	BordereauWithState,
	TreeRow,
} from '../../../interfaces/pieces/TreeRow';
import useLiquidationTreeviewStore from '../../../stores/liquidationTreeviewStore';
import useActionsLignes from './hooks/useActionsLignes';
import useChargementRacines from './hooks/useChargementRacines';
import useDefilementConserve from './hooks/useDefilementConserve';
import useEntitesVideesParFiltre from './hooks/useEntitesVideesParFiltre';
import useEtatsVisibles from './hooks/useEtatsVisibles';
import useRelectureFiche from './hooks/useRelectureFiche';
import LiquidationTreeviewLoader from './LiquidationTreeviewLoader';

const TABLE_LOCALE = { emptyMessage: 'Aucune donnée' };

const NO_KEYS: ReadonlySet<string> = new Set();

const { getState } = useLiquidationTreeviewStore;

interface PieceTreeViewProps {
	type: TreeViewType;
	height: number;
	filterSens: SensOptions;
	anneeExercice: number;
	filtreBordereauState?: number;
	onVisibleStatesChange?: (states: Set<BordereauStateType>) => void;
}

/**
 * Arbre entités > bordereaux > pièces d'une année d'exercice. Son état (lignes chargées et dépliées, défilement)
 * vit dans useLiquidationTreeviewStore : il est retrouvé tel quel au retour d'une fiche, et les modifications que
 * la fiche a signalées sont relues à l'affichage. Chaque comportement est dans un hook (./hooks) : chargement des
 * entités, actions sur les lignes, relecture après la fiche, entités vidées par les filtres, états visibles,
 * défilement conservé.
 */
const LiquidationTreeview = ({
	type,
	height,
	filterSens,
	anneeExercice,
	filtreBordereauState,
	onVisibleStatesChange,
}: PieceTreeViewProps) => {
	const arbre = useLiquidationTreeviewStore((state) =>
		state.annees.get(anneeExercice),
	);
	const index = arbre?.index ?? TreeIndex.EMPTY;
	const expandedKeys = arbre?.expandedKeys ?? NO_KEYS;
	const loadingKeys = arbre?.loadingKeys ?? NO_KEYS;
	const isRacinesChargees = arbre?.isRacinesChargees ?? false;

	const dataTreeView = React.useMemo(
		() => TreeViewFactory.newInstance(type),
		[type],
	);

	const treeRowFilter = React.useMemo(
		() => new TreeRowFilter(filtreBordereauState, filterSens),
		[filtreBordereauState, filterSens],
	);

	// table recréée à chaque changement de filtre : elle repart en haut (useDefilementConserve)
	// biome-ignore lint/correctness/useExhaustiveDependencies: clé renouvelée quand les filtres changent
	const treeViewKey = React.useMemo(
		() => newIdentifiant(),
		[filtreBordereauState, filterSens],
	);

	const [userRole] = useUserRole();
	const history = useHistory();

	useChargementRacines(anneeExercice);
	useRelectureFiche(anneeExercice, dataTreeView);
	useEtatsVisibles(index, filterSens, onVisibleStatesChange);
	const signalerDepliage = useEntitesVideesParFiltre({
		anneeExercice,
		index,
		expandedKeys,
		loadingKeys,
		treeRowFilter,
	});
	const { actions, handleExpand } = useActionsLignes(
		anneeExercice,
		dataTreeView,
		signalerDepliage,
	);
	const enregistrerDefilement = React.useCallback(
		(position: number) => getState().setScrollTop(anneeExercice, position),
		[anneeExercice],
	);
	const { tableRef, onScroll } = useDefilementConserve<
		TableInstance<TreeRow, string>
	>({
		positionInitiale: arbre?.scrollTop ?? 0,
		isPret: isRacinesChargees,
		cleTable: treeViewKey,
		enregistrer: enregistrerDefilement,
	});

	const visibleData = React.useMemo(
		() => index.buildVisibleTree(expandedKeys, treeRowFilter.acceptChild),
		[index, expandedKeys, treeRowFilter],
	);

	const expandedRowKeysArray = React.useMemo(
		() => Array.from(expandedKeys),
		[expandedKeys],
	);

	/** Clic sur une ligne : une pièce se déplie seule (une à la fois), les autres lignes se déplient ou se replient. */
	const handleClick = React.useCallback(
		(rowData: TreeRow): void => {
			if (rowData.nodeType === TreeNodeType.MOUVEMENT) {
				getState().toggleMouvementDeplie(anneeExercice, rowData.rowKey);
				return;
			}
			const isExpanded = !getState()
				.annees.get(anneeExercice)
				?.expandedKeys.has(rowData.rowKey);
			if (isExpanded) {
				signalerDepliage(rowData);
			}
			getState().setExpanded(anneeExercice, rowData.rowKey, isExpanded);
		},
		[anneeExercice, signalerDepliage],
	);

	/** Double-clic sur une pièce : sa fiche, en lecture seule si son bordereau n'est pas en cours. */
	const handleDoubleClick = React.useCallback(
		(rowData: TreeRow): void => {
			if (rowData.nodeType !== TreeNodeType.MOUVEMENT) return;

			const parentBordereau: BordereauWithState = getState()
				.annees.get(anneeExercice)
				?.index.getParent(rowData.rowKey)?.bordereau;

			if (parentBordereau?.state?.state === BordereauStateType.EN_COURS) {
				history.push(`/liquidations/${rowData.identifiant}`);
			} else {
				history.push(`/liquidations/${rowData.identifiant}`, {
					readonly: true,
				});
			}
		},
		[anneeExercice, history],
	);

	const renderOptions = React.useMemo(
		() => ({ filterSens, index }),
		[filterSens, index],
	);

	const renderTreeToggle = React.useCallback(
		(_expandButton: React.ReactNode, rowData: TreeRow, isExpanded: boolean) =>
			dataTreeView.renderTreeToggle(
				isExpanded,
				loadingKeys.has(rowData.rowKey),
			),
		[dataTreeView, loadingKeys],
	);

	const getRowHeight = React.useCallback(
		(rowData: TreeRow) =>
			dataTreeView?.getRowHeight(rowData, expandedKeys.has(rowData?.rowKey)),
		[dataTreeView, expandedKeys],
	);

	if (!isRacinesChargees) {
		return <LiquidationTreeviewLoader />;
	}

	return (
		<Table
			key={treeViewKey}
			ref={tableRef}
			isTree
			data-testid="liquidationTreeview"
			locale={TABLE_LOCALE}
			rowKey="rowKey"
			hover={false}
			data={visibleData}
			expandedRowKeys={expandedRowKeysArray}
			className="liquidation-treeview"
			rowExpandedHeight={500}
			height={height - 150}
			virtualized
			showHeader={false}
			renderTreeToggle={renderTreeToggle}
			shouldUpdateScroll={false}
			rowHeight={getRowHeight}
			onExpandChange={handleExpand}
			onScroll={onScroll}
		>
			{({ Cell, Column, HeaderCell }) => (
				<Column flexGrow={1} verticalAlign="middle">
					<HeaderCell> </HeaderCell>
					<Cell>
						{(rowData: TreeRow) =>
							dataTreeView?.renderRow(
								rowData,
								expandedKeys.has(rowData.rowKey),
								userRole,
								() => handleClick(rowData),
								() => handleDoubleClick(rowData),
								actions,
								renderOptions,
							)
						}
					</Cell>
				</Column>
			)}
		</Table>
	);
};

export default LiquidationTreeview;
